import { useQueryClient } from '@tanstack/react-query';
import { getCachedThemeLogoUrl } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_MENTIONS_PER_REQUEST, MentionCandidate } from '../types';
import { useMentionEntitySource } from '../useMentionEntitySource';
import { useMentionWatchlistSource } from '../useMentionWatchlistSource';
import { placeCaretAfterChip } from './caret';
import { createMentionChipElement } from './dom-chip';
import {
  flattenMentionSections,
  isMentionSectionTitleRow,
  MentionDropdownItem,
  MentionPopupSection,
} from './MentionPopup';
import {
  ActiveTrigger,
  findActiveTrigger,
  trimMentionQuery,
} from './trigger-detection';

interface MentionDropdownApi {
  handleEnter: () => void;
  focusedItemIndex: number;
  setFocusedItemIndex: (index: number) => void;
  setCurrentAction: (action: 'hover' | 'focus') => void;
  getFocusedIndexForArrowKey: (key: 'ArrowUp' | 'ArrowDown') => number;
}

export interface UseMentionPopupArgs {
  editorRef: RefObject<HTMLDivElement | null>;
  emitChange: () => void;
}

export interface UseMentionPopupResult {
  activeTrigger: ActiveTrigger | null;
  setActiveTrigger: (trigger: ActiveTrigger | null) => void;
  applyTrigger: (trigger: ActiveTrigger) => void;
  refreshTriggerFromEditor: (root: HTMLElement) => void;
  dropdownItems: MentionDropdownItem[];
  isSearching: boolean;
  hadMatchForActiveTrigger: boolean;
  dropdownApiRef: RefObject<MentionDropdownApi>;
  insertCandidate: (candidate: MentionCandidate) => void;
  handlePopupKeyDown: (e: KeyboardEvent<HTMLDivElement>) => boolean;
  clearSources: () => void;
}

export const useMentionPopup = ({
  editorRef,
  emitChange,
}: UseMentionPopupArgs): UseMentionPopupResult => {
  const { t } = useTranslation(['chat', 'countries', 'analyze', 'widget']);
  const queryClient = useQueryClient();
  const language = useLanguageStore(state => state.languageSetting);
  const dropdownApiRef = useRef<MentionDropdownApi>({
    handleEnter: () => {},
    focusedItemIndex: -1,
    setFocusedItemIndex: () => {},
    setCurrentAction: () => {},
    getFocusedIndexForArrowKey: () => -1,
  });
  // Sticky match for the active trigger — see hadMatchForActiveTrigger below.
  const matchRef = useRef<{
    trigger: '@' | '#';
    query: string;
    matched: boolean;
  } | null>(null);
  const [activeTrigger, setActiveTrigger] = useState<ActiveTrigger | null>(
    null
  );

  const entitySource = useMentionEntitySource(activeTrigger?.trigger === '@');
  const watchlistSource = useMentionWatchlistSource();

  const applyTrigger = useCallback(
    (trigger: ActiveTrigger) => {
      setActiveTrigger(prev => {
        if (
          prev &&
          prev.trigger === trigger.trigger &&
          prev.query === trigger.query &&
          prev.startOffset === trigger.startOffset &&
          prev.startNode === trigger.startNode &&
          prev.unresolvedElement === trigger.unresolvedElement
        ) {
          return prev;
        }
        return trigger;
      });
      const searchableQuery = trimMentionQuery(trigger.query);
      if (trigger.trigger === '@') {
        entitySource.setQuery(searchableQuery);
      } else {
        watchlistSource.setQuery(searchableQuery);
      }
    },
    [entitySource, watchlistSource]
  );

  const refreshTriggerFromEditor = useCallback(
    (root: HTMLElement) => {
      const trigger = findActiveTrigger(root);
      if (trigger) {
        applyTrigger(trigger);
        return;
      }
      setActiveTrigger(null);
    },
    [applyTrigger]
  );

  const sections: MentionPopupSection[] = useMemo(() => {
    if (!activeTrigger) {
      return [];
    }
    if (activeTrigger.trigger === '@') {
      if (!trimMentionQuery(activeTrigger.query)) {
        return [
          {
            label: t('chat:mentions.recentSection'),
            items: entitySource.recent,
          },
        ];
      }
      return [
        {
          label: t('chat:mentions.assetsSection'),
          items: entitySource.entities,
        },
        { label: t('chat:mentions.themesSection'), items: entitySource.themes },
      ];
    }
    return [
      { label: t('chat:mentions.listsSection'), items: watchlistSource.lists },
      {
        label: t('chat:mentions.publicListsSection'),
        items: watchlistSource.publicLists,
      },
    ];
  }, [
    activeTrigger,
    entitySource.entities,
    entitySource.themes,
    entitySource.recent,
    watchlistSource.lists,
    watchlistSource.publicLists,
    t,
  ]);

  const dropdownItems: MentionDropdownItem[] = useMemo(
    () => flattenMentionSections(sections),
    [sections]
  );

  // Dropdown only paints the focus highlight while its internal
  // `currentAction` is 'focus' — a prior mouse hover flips it to 'hover'
  // and nothing resets it, so the auto-focused first item silently stops
  // rendering as focused after any hover. Force it back on every refresh
  // (new trigger or new results) so the highlight is never invisible.
  useEffect(() => {
    dropdownApiRef.current.setCurrentAction('focus');
  }, [activeTrigger, dropdownItems]);

  const isSearching = activeTrigger
    ? activeTrigger.trigger === '@'
      ? entitySource.loading
      : watchlistSource.loading
    : false;

  if (!activeTrigger) {
    matchRef.current = null;
  } else {
    const { trigger, query } = activeTrigger;
    const prev = matchRef.current;
    if (!prev || prev.trigger !== trigger || prev.query !== query) {
      const inherit =
        !!prev &&
        prev.matched &&
        prev.trigger === trigger &&
        prev.query.length > 0 &&
        query.startsWith(prev.query);
      matchRef.current = { trigger, query, matched: inherit };
    }
    if (!isSearching) {
      const hasCandidates =
        trimMentionQuery(query).length > 0 &&
        dropdownItems.some(item => !isMentionSectionTitleRow(item));
      matchRef.current = { trigger, query, matched: hasCandidates };
    }
  }

  const hadMatchForActiveTrigger = matchRef.current?.matched ?? false;

  const insertCandidate = useCallback(
    (candidate: MentionCandidate) => {
      const trigger = activeTrigger;
      const root = editorRef.current;
      if (!trigger || !root) {
        return;
      }

      if (
        root.querySelectorAll('[data-mention-chip]').length >=
        MAX_MENTIONS_PER_REQUEST
      ) {
        setActiveTrigger(null);
        return;
      }

      const resolvedCandidate =
        candidate.kind === 'theme' && !candidate.theme.logo_url
          ? {
              ...candidate,
              theme: {
                ...candidate.theme,
                logo_url: getCachedThemeLogoUrl(
                  queryClient,
                  candidate.id,
                  language
                ),
              },
            }
          : candidate;

      const chip = createMentionChipElement(resolvedCandidate);
      const space = document.createTextNode('\u00a0');

      if (trigger.unresolvedElement?.isConnected) {
        trigger.unresolvedElement.replaceWith(chip, space);
        placeCaretAfterChip(chip);
        setActiveTrigger(null);
        root.focus();
        emitChange();
        return;
      }

      if (trigger.startNode == null || trigger.startOffset == null) {
        setActiveTrigger(null);
        return;
      }

      const range = document.createRange();
      const nodeLength = trigger.startNode.nodeValue?.length ?? 0;
      const endOffset = Math.min(
        trigger.startOffset + 1 + trigger.query.length,
        nodeLength
      );
      range.setStart(trigger.startNode, trigger.startOffset);
      range.setEnd(trigger.startNode, endOffset);
      range.deleteContents();

      range.insertNode(space);
      range.insertNode(chip);

      root.normalize();
      placeCaretAfterChip(chip);

      setActiveTrigger(null);
      root.focus();
      emitChange();
    },
    [activeTrigger, editorRef, emitChange, language, queryClient]
  );

  const skipTitleRows = useCallback(
    (startIndex: number, direction: 'ArrowUp' | 'ArrowDown'): number => {
      let index = startIndex;
      for (
        let guard = 0;
        guard < dropdownItems.length &&
        dropdownItems[index] &&
        isMentionSectionTitleRow(dropdownItems[index]);
        guard++
      ) {
        index =
          direction === 'ArrowDown'
            ? (index + 1) % dropdownItems.length
            : (index - 1 + dropdownItems.length) % dropdownItems.length;
      }
      return index;
    },
    [dropdownItems]
  );

  const selectFocusedMention = useCallback((): boolean => {
    const api = dropdownApiRef.current;
    let index = api.focusedItemIndex;
    if (
      dropdownItems.length > 0 &&
      (index < 0 || isMentionSectionTitleRow(dropdownItems[index]))
    ) {
      index = skipTitleRows(Math.max(index, 0), 'ArrowDown');
    }

    const item = dropdownItems[index];
    if (item && !isMentionSectionTitleRow(item)) {
      insertCandidate(item);
      return true;
    }

    setActiveTrigger(null);
    return true;
  }, [dropdownItems, insertCandidate, skipTitleRows]);

  const handlePopupKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): boolean => {
      const api = dropdownApiRef.current;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        api.setCurrentAction('focus');
        api.setFocusedItemIndex(
          skipTitleRows(api.getFocusedIndexForArrowKey(e.key), e.key)
        );
        return true;
      }
      if (e.key === 'Escape') {
        setActiveTrigger(null);
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (e.nativeEvent.isComposing) {
          return false;
        }
        return selectFocusedMention();
      }
      return false;
    },
    [selectFocusedMention, skipTitleRows]
  );

  const clearSources = useCallback(() => {
    entitySource.clear();
    watchlistSource.clear();
  }, [entitySource, watchlistSource]);

  return {
    activeTrigger,
    setActiveTrigger,
    applyTrigger,
    refreshTriggerFromEditor,
    dropdownItems,
    isSearching,
    hadMatchForActiveTrigger,
    dropdownApiRef,
    insertCandidate,
    handlePopupKeyDown,
    clearSources,
  };
};
